import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { PendingReceipt, PendingReceiptInput } from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';

interface PendingReceiptsResponse {
  pendingReceipts: PendingReceipt[];
}

interface PendingReceiptResponse {
  pendingReceipt: PendingReceipt;
}

export const pendingReceiptKeys = {
  all: ['pendingReceipts'] as const,
  list: (jobId: string) => [...pendingReceiptKeys.all, jobId] as const,
};

export function usePendingReceipts(jobId: string | undefined) {
  return useQuery({
    queryKey: pendingReceiptKeys.list(jobId!),
    queryFn: async () => {
      const response = await apiClient.get<PendingReceiptsResponse>('/pending-receipts', {
        params: { jobId },
      });
      return response.pendingReceipts;
    },
    enabled: !!jobId,
  });
}

export function useCreatePendingReceipt() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: PendingReceiptInput) => {
      const response = await apiClient.post<PendingReceiptResponse>('/pending-receipts', data);
      return response.pendingReceipt;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: pendingReceiptKeys.list(variables.jobId) });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create pending receipt.',
        variant: 'destructive',
      });
    },
  });
}

export function useDismissAllPendingReceipts(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/pending-receipts', undefined, { params: { jobId } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pendingReceiptKeys.list(jobId) });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to dismiss pending receipts.',
        variant: 'destructive',
      });
    },
  });
}

export function useDeletePendingReceipt(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/pending-receipts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pendingReceiptKeys.list(jobId) });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete pending receipt.',
        variant: 'destructive',
      });
    },
  });
}
